import React, { useState } from 'react';

import EditChannelVisibilityModal, { checkEditableChannels } from '../../components/EditChannelVisibilityModal';
import { getChannelsForStockItem } from '@services/stockChannels';
import { useCheckPermission } from '@services/auth/hooks';

import AuthorizationTooltip from '@components/AuthorizationTooltip';
import { Button, Card, StatusIcon } from '@nearst/ui';

import styles from './ChannelVisibilityCard.module.scss';

const getDescription = (stockItem) => {
	if (stockItem.issues?.length > 0) return 'Product issues are affecting visibility in your channels.';
	if (!stockItem.enabled) return 'Your product is currently hidden across all enabled channels.';
	if (stockItem.enabled) return 'Your product is displayed in all enabled channels.';
	return undefined;
};

const getStatus = (stockItem, stockChannel) => {
	if (!stockItem.enabled) return 'hidden';
	if (!stockChannel.enabled) return 'hidden';
	if (!stockChannel) return 'hidden';
	if (stockChannel.errors?.length > 0) return 'error';
	if (stockChannel.warnings?.length > 0) return 'warning';
	return 'active';
};

const canEditVisibility = (stockItem, channelStatuses) => {
	if (stockItem.issueDefinitions?.some((issue) => issue.channels.nearst?.severity === 'error')) return false;
	if (!stockItem.enabled) return false;
	const editableChannels = checkEditableChannels(channelStatuses);
	if (!editableChannels.length) return false;
	return true;
};

const ChannelVisibilityItem = ({ stockItem, stockChannel }) => {
	const channelStatus = getStatus(stockItem, stockChannel);

	return (
		<>
			<StatusIcon status={channelStatus} />
			<span data-status={channelStatus}>{stockChannel.label}</span>
		</>
	);
};

const ChannelVisibilityCard = ({ shopId, stockItem, channels }) => {
	const [editModalShown, setEditModalShown] = useState(false);

	const authorized = useCheckPermission('org:channels:manage');

	const channelDescription = getDescription(stockItem);
	const stockChannels = getChannelsForStockItem(shopId, stockItem, channels);

	return (
		<Card title="Channel visibility" data-cy="inventory-item-channel-visibility">
			<EditChannelVisibilityModal
				stockItems={[stockItem]}
				isOpen={editModalShown}
				shopId={shopId}
				channelStatuses={stockChannels}
				onDismiss={() => setEditModalShown(false)}
			/>

			{channelDescription && <p className={styles.channelDescription}>{channelDescription}</p>}
			{stockChannels?.map((stockChannel) => {
				return (
					<div key={stockChannel.label} className={styles.channelItem}>
						<ChannelVisibilityItem stockItem={stockItem} stockChannel={stockChannel} />
					</div>
				);
			})}
			{canEditVisibility(stockItem, stockChannels) && (
				<div className={styles.buttonContainer}>
					<AuthorizationTooltip authorized={authorized} tooltipContent={`You don't have permission to edit this`}>
						<Button onClick={() => setEditModalShown(true)} disabled={!authorized}>
							Edit
						</Button>
					</AuthorizationTooltip>
				</div>
			)}
		</Card>
	);
};

export default ChannelVisibilityCard;
